import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { TextBoxComponent } from '@progress/kendo-angular-inputs';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { first, switchMap } from 'rxjs/operators';
import { AuthenticationService } from '../../auth/authentication.service';
import { AuthenticatedUser } from '../../models/auth/authenticated-user.model';
import { LocalStorageService } from '../../services/local-storage.service';
import { CustomMessagesService } from '../../services/translation/custom-messages.service';

@UntilDestroy()
@Component({
    selector: 'clfl-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, AfterViewInit {
    @ViewChild('username') public username!: TextBoxComponent;
    @ViewChild('password') public password!: TextBoxComponent;

    loginForm!: FormGroup;
    loading = false;
    submitted = false;
    returnUrl!: string;
    isLoading = false;
    error = '';

    constructor(
        private formBuilder: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private authenticationService: AuthenticationService,
        private translate: TranslateService,
        private recaptchaV3Service: ReCaptchaV3Service,
        private msgService: CustomMessagesService,
        private localStorageService: LocalStorageService
    ) {
        if (this.authenticationService.currentUserValue) {
            this.router.navigate(['/']);
        } else {
            this.authenticationService.isAuthenticated = false;
        }
    }

    ngOnInit(): void {
        this.loginForm = this.formBuilder.group({
            username: ['', Validators.required],
            password: ['', Validators.required]
        });

        // get return url from route parameters or default to '/'
        this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
    }

    ngAfterViewInit(): void {
        this.password.input.nativeElement.type = 'password';
    }

    public getCurrentLang() {
        return this.translate.currentLang ? this.translate.currentLang : this.translate.defaultLang;
    }

    public toggleVisibility(): void {
        const inputEl = this.password.input.nativeElement;
        inputEl.type = inputEl.type === 'password' ? 'text' : 'password';
    }

    public onSubmit() {
        this.submitted = true;

        // stop here if form is invalid
        if (this.loginForm.invalid) {
            return;
        }

        this.loading = true;

        this.localStorageService.removeCurrentUser();
        this.recaptchaV3Service
            .execute('importantAction')
            .pipe(
                untilDestroyed(this),
                switchMap((token: string) => {
                    console.debug(`Token [${token}] generated`);

                    return this.authenticationService
                        .login(this.username.value, this.password.value, '' /*this.f.code.value*/, this.getCurrentLang(), token)
                        .pipe(first());
                }),
                switchMap((user) => this.authenticationService.populateUserCompanies(user))
            )
            .subscribe({
                next: (user: AuthenticatedUser) => {
                    if (this.returnUrl != '/') {
                        this.router.navigate([this.returnUrl]);
                    } else {
                        this.router.navigate(['home']);
                    }
                },
                error: (error) => {
                    this.error = error;
                    console.log(error);
                    if (error && error.includes('401')) {
                        this.error = this.msgService.translate('cf-document.tab.files.column.downloadError');
                    }
                    this.loading = false;
                }
            });
    }

    public clearForm() {
        this.loginForm.reset();
        this.error = '';
    }
}
